import type { UiSlice } from '@/shared/lib/zustand-types';
import type { DialogEntry, DialogKind, DialogPropsMap } from './types';

export type DialogSlice = {
  /**
   * Stack of currently-open dialogs. Top of stack (last index) renders on
   * top. DialogHost iterates this array and translates each entry into a
   * mounted React component via the registry.
   */
  dialogs: DialogEntry[];

  /**
   * Push a new dialog onto the stack. Returns the id so callers can close
   * it later if they hold the handle. Most callers only need the kind —
   * `closeDialogByKind` covers them without tracking the id.
   */
  openDialog: <K extends DialogKind>(kind: K, props: DialogPropsMap[K]) => string;

  /** Close a single dialog by id. Defaults to the topmost dialog. */
  closeDialog: (id?: string) => void;

  /**
   * Patch the props of an open dialog in place. Useful when the parent owns
   * authoritative state (e.g. mugshot config) and needs the dialog to
   * receive the latest snapshot without a full close/reopen cycle.
   */
  updateDialog: <K extends DialogKind>(id: string, props: Partial<DialogPropsMap[K]>) => void;

  /** Close every dialog of the given kind (rare — usually one at a time). */
  closeDialogByKind: (kind: DialogKind) => void;

  /** Close every dialog. Used on route change / hard reset. */
  closeAllDialogs: () => void;
};

let counter = 0;
const nextId = (kind: DialogKind): string => `${kind}:${++counter}`;

export const createDialogSlice: UiSlice<DialogSlice> = (set) => ({
  dialogs: [],

  openDialog: (kind, props) => {
    const id = nextId(kind);
    set((s) => {
      // immer's writable cast — props are stored as immutable references; we
      // never mutate them in place, only swap via `updateDialog`.
      (s.dialogs as DialogEntry[]).push({ id, kind, props } as DialogEntry);
    });
    return id;
  },

  closeDialog: (id) =>
    set((s) => {
      if (s.dialogs.length === 0) return;
      if (id === undefined) {
        s.dialogs.pop();
        return;
      }
      const i = s.dialogs.findIndex((d) => d.id === id);
      if (i >= 0) s.dialogs.splice(i, 1);
    }),

  updateDialog: (id, props) =>
    set((s) => {
      const entry = s.dialogs.find((d) => d.id === id);
      if (!entry) return;
      entry.props = { ...entry.props, ...props };
    }),

  closeDialogByKind: (kind) =>
    set((s) => {
      s.dialogs = s.dialogs.filter((d) => d.kind !== kind);
    }),

  closeAllDialogs: () =>
    set((s) => {
      s.dialogs = [];
    }),
});
